import { useEffect, useState } from "react";
import {
    Box, Grid, FormControl, InputLabel, Select, Button, Card, CardActions,
    CardContent, CardMedia, Container, Divider, Backdrop, CircularProgress,
    Typography
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckAttr from "../../../../utils/CheckAttr";
import { DataRef } from "../../../../types/DataRef";
import { AlertType } from "../../../../types/AlertType";
import { SnackBarAlert } from "../../../element/SnackBarAlert";
import { downloadFile } from "../../../../utils/utils";
import { MODE_BUDGET } from "../../../../constants/Constants";
import Cookies from "universal-cookie";
import axios, { AxiosInstance } from "axios";

interface InputValidation {
    yearProd: boolean;
    yearBudget: boolean;
    consolidation: boolean;
}

interface ExtractData {
    yearProd: Dayjs | null;
    yearBudget: Dayjs | null;
    consolidation?: "Annuelle" | "Mensuelle";
    pays?: number;
    bu?: number;
    client?: string;
    operation?: string;
}

interface DataRefClientWithBudget {
    id: number;
    libelle: string;
    is_clientBudget: number;
}

interface DataRefOperationWithBudget {
    id: number;
    libelle: string;
    is_operationBudget: number;
}

const initialData: ExtractData = {
    yearProd: null,
    yearBudget: null,
    consolidation: 'Annuelle',
    pays: undefined,
    bu: undefined,
    client: undefined,
    operation: undefined
};

const resetInputValidation: InputValidation = {
    yearProd: false,
    yearBudget: false,
    consolidation: false
};

const model = "Budget";

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const ExtractionBudget = () => {
    const cookies = new Cookies();
    const token: string = cookies.get("jwt");

    const http: AxiosInstance = axios.create({
        baseURL: urlBaseApi,
        // timeout: 1000,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    const [alertMessage, setAlerteMessage] = useState<string>('');
    const [openSnack, setOpenSnack] = useState<boolean>(false);
    const [alertType, setAlertType] = useState<AlertType>("success");

    const [loading, setLoading] = useState<boolean>(true);

    const [dataPays, setDataPays] = useState<DataRef[]>([]);
    const [dataBusinessUnits, setDataBusinessUnits] = useState<DataRef[]>([]);
    const [dataClients, setDataClients] = useState<DataRefClientWithBudget[]>([]);
    const [dataOperations, setDataOperations] = useState<DataRefOperationWithBudget[]>([]);

    const [extractionData, setExtractionData] = useState<ExtractData>(initialData);

    const [errorInput, setErrorInput] = useState<InputValidation>(resetInputValidation);

    useEffect(() => {
        http.get('pivot-client-bu/get-all-exist-pays-in-association')
            .then(res => {
                Array.isArray(res.data) && setDataPays(res.data);
                setLoading(false);
            }).catch(error => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        setExtractionData({
            ...extractionData,
            bu: undefined,
            client: undefined,
            operation: undefined
        });
        setDataBusinessUnits([]);
        setDataClients([]);
        setDataOperations([]);

        if (extractionData?.pays) {
            // Récupère les clients du pays sélectionné
            http.get(`bu/findby-pays/${extractionData?.pays}`).then((res) => {
                Array.isArray(res.data) && setDataBusinessUnits(res.data);
            }).catch((error) => {
                console.log(error);
            });
        }
    }, [extractionData?.pays]);

    useEffect(() => {
        setExtractionData({
            ...extractionData,
            client: undefined,
            operation: undefined
        });
        setDataClients([]);
        setDataOperations([]);

        if (extractionData?.bu) {
            // Récupère les clients du pays sélectionné
            http.post("budget/get/client/prod-budget/by/pays/bu", {
                pays: extractionData?.pays,
                bu: extractionData?.bu
            }).then((res) => {
                Array.isArray(res.data) && setDataClients(res.data);
            }).catch((error) => {
                console.log(error);
            });
        }
    }, [extractionData?.bu]);

    useEffect(() => {
        setExtractionData({
            ...extractionData,
            operation: undefined
        });
        setDataOperations([]);

        if (extractionData?.client) {
            const splitedClientValue = extractionData?.client?.split('_');

            // Récupère les clients du pays sélectionné
            http.post('budget/get/operation', {
                pays: extractionData?.pays,
                bu: extractionData?.bu,
                is_client_budget: splitedClientValue[0] === MODE_BUDGET,
                client: Number(splitedClientValue[1])
            }).then((res) => {
                Array.isArray(res.data) && setDataOperations(res.data);
            }).catch((error) => {
                console.log(error);
            });
        }
    }, [extractionData?.client]);

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        setExtractionData({
            ...extractionData,
            [name]: value
        });
    };

    const handleChangeYearProd = (e: any) => {
        setExtractionData({
            ...extractionData,
            yearProd: dayjs(e)
        });
    };

    const handleChangeYearBudget = (e: any) => {
        setExtractionData({
            ...extractionData,
            yearBudget: dayjs(e)
        });
    };

    const isValidate = () => {
        // Reinitialise les variables des validations
        setErrorInput(resetInputValidation);
        if (!CheckAttr(extractionData?.yearProd) || !CheckAttr(extractionData?.yearBudget) || !CheckAttr(extractionData?.consolidation)) {
            !CheckAttr(extractionData?.yearProd) && setErrorInput((prev: InputValidation) => ({ ...prev, yearProd: true }));
            !CheckAttr(extractionData?.yearBudget) && setErrorInput((prev: InputValidation) => ({ ...prev, yearBudget: true }));
            !CheckAttr(extractionData?.consolidation) && setErrorInput((prev: InputValidation) => ({ ...prev, consolidation: true }));

            setAlerteMessage("Merci de remplir les champs en rouge SVP!");
            setAlertType("warning");
            setOpenSnack(true);

            return false;
        }

        return true;
    }

    const handleExtract = () => {
        if (isValidate()) {
            setLoading(true);

            // Valeur du client
            let is_client_budget;
            let client;
            if (extractionData?.client) {
                const splitedClientValue = extractionData?.client?.split('_');
                is_client_budget = splitedClientValue[0] === MODE_BUDGET;
                client = Number(splitedClientValue[1]);
            }

            // Valeur operation
            let is_operation_budget;
            let operation;
            if (extractionData?.operation) {
                const splitedOperationValue = extractionData?.operation?.split('_');
                is_operation_budget = splitedOperationValue[0] === MODE_BUDGET;
                operation = Number(splitedOperationValue[1]);
            }

            const data = {
                annee_realise: extractionData?.yearProd?.format('YYYY'),
                anneBudget: extractionData?.yearBudget?.format('YYYY'),
                consolidation: extractionData?.consolidation,
                pays: extractionData?.pays,
                bu: extractionData?.bu,
                is_client_budget,
                client,
                is_operation_budget,
                operation
            };

            http.post("extraction/realiser-vs-budget", data)
                .then(res => {
                    // On télécharge le fichier
                    //const { sucess, filename } = res.data;
                    // sucess && downloadFile(filename as string);

                    setAlerteMessage("Extraction efféctuée avec succès !");
                    setAlertType("success");
                    setOpenSnack(true);

                    downloadFile(res.data as string);
                }).catch((error) => {
                    console.log(error);
                    setAlertType("error");
                    setOpenSnack(true);
                }).finally(() => {
                    setLoading(false);
                });
        }
    };

    const minDateOfBudgetYear = dayjs(new Date(new Date().getFullYear(), 0, 1));
    const maxDateOfBudgetYear = dayjs(new Date((new Date().getFullYear() + 1), 11, 31));

    const minDateOfRealisedYear = ((new Date().getFullYear() - 1) < 2024) ? minDateOfBudgetYear : dayjs(new Date((new Date().getFullYear() - 1), 0, 1));
    const maxDateOfRealisedYear = dayjs(new Date(new Date().getFullYear(), 11, 31));

    const handleCloseAlert = () => {
        setOpenSnack(false)
    }

    const stackAlert: JSX.Element = SnackBarAlert(openSnack, handleCloseAlert, alertType, alertMessage);

    return (
        <>
            <Container maxWidth="md" sx={{ mt: 10 }}>
                <Card sx={{ maxWidth: 845 }}>
                    <CardMedia
                        component="img"
                        image="/images/extraction_budget.png"
                        alt="green iguana"
                        sx={{ display: { xs: 'none', sm: 'flex' }, height: 140 }}
                    />

                    <CardContent>
                        <Box>
                            <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }} sx={{ mt: 1 }}>
                                <Grid item xs={6} sm={5.5} md={5.5}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="Réalisé Année..."
                                            views={["year"]}
                                            value={extractionData?.yearProd}
                                            onChange={handleChangeYearProd}
                                            minDate={minDateOfRealisedYear}
                                            maxDate={maxDateOfRealisedYear}
                                            slotProps={{
                                                textField: {
                                                    color: "warning",
                                                    fullWidth: true,
                                                    error: errorInput?.yearProd
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                <Grid item xs={6} sm={1} md={1}>
                                    <Box display="flex" justifyContent="center" sx={{ pt: { sm: 1, md: 2 } }}>
                                        <Typography variant="button">
                                            VS
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={6} sm={5.5} md={5.5}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="Budget Année..."
                                            views={["year"]}
                                            value={extractionData?.yearBudget}
                                            onChange={handleChangeYearBudget}
                                            minDate={dayjs(minDateOfBudgetYear)}
                                            maxDate={dayjs(maxDateOfBudgetYear)}
                                            slotProps={{
                                                textField: {
                                                    color: "warning",
                                                    fullWidth: true,
                                                    error: errorInput?.yearBudget
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </Grid>

                            <Grid container columns={{ xs: 6, sm: 12, md: 12 }} sx={{ mt: 3 }}>
                                <Grid item xs={6} sm={12} md={12}>
                                    <FormControl
                                        variant="outlined"
                                        fullWidth
                                        sx={{ marginTop: 1 }}
                                    >
                                        <InputLabel
                                            htmlFor="consolidation"
                                            color="warning"
                                            shrink={CheckAttr(extractionData?.consolidation)}
                                        >
                                            Consolidation
                                        </InputLabel>
                                        <Select
                                            id="consolidation"
                                            native
                                            color="warning"
                                            value={extractionData?.consolidation}
                                            error={errorInput?.consolidation}
                                            onChange={handleChange}
                                            label="Consolidation"
                                            inputProps={{
                                                name: "consolidation",
                                            }}
                                        >
                                            <option value="Annuelle">Annuelle</option>
                                            <option value="Mensuelle">Mensuelle</option>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Divider sx={{ mt: 5, mb: 1 }}>Filtre</Divider>

                            <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }}>
                                <Grid item xs={6} sm={6} md={6}>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        sx={{ marginTop: 1 }}
                                    >
                                        <InputLabel
                                            htmlFor="pays"
                                            color="warning"
                                            shrink={CheckAttr(extractionData?.pays)}
                                        >
                                            Pays de production
                                        </InputLabel>
                                        <Select
                                            id="pays"
                                            native
                                            color="warning"
                                            value={extractionData?.pays}
                                            onChange={handleChange}
                                            label="Pays de production"
                                            inputProps={{
                                                name: "pays",
                                            }}
                                        >
                                            <option value=""></option>
                                            {dataPays.map((pays) => (
                                                <option key={pays?.id} value={pays?.id}>
                                                    {pays?.libelle}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={6} md={6}>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        sx={{ marginTop: 1 }}
                                    >
                                        <InputLabel
                                            htmlFor="bu"
                                            color="warning"
                                            shrink={CheckAttr(extractionData?.bu)}
                                        >
                                            Business Unit
                                        </InputLabel>
                                        <Select
                                            id="bu"
                                            native
                                            color="warning"
                                            value={extractionData?.bu}
                                            onChange={handleChange}
                                            label="Business Unit"
                                            inputProps={{
                                                name: "bu",
                                            }}
                                        >
                                            <option value=""></option>
                                            {dataBusinessUnits.map((bu) => (
                                                <option key={bu?.id} value={bu?.id}>
                                                    {bu?.libelle}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={6} md={6}>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        sx={{ marginTop: 1 }}
                                    >
                                        <InputLabel
                                            htmlFor="client"
                                            color="warning"
                                            shrink={CheckAttr(extractionData?.client)}
                                        >
                                            Client
                                        </InputLabel>
                                        <Select
                                            id="client"
                                            native
                                            color="warning"
                                            value={extractionData?.client}
                                            onChange={handleChange}
                                            label="Client"
                                            inputProps={{
                                                name: "client",
                                            }}
                                        >
                                            <option value={undefined}></option>
                                            {dataClients.map((client) => (
                                                <option key={client?.id} value={(client?.is_clientBudget === 1) ? `budget_${client?.id}` : `prod_${client?.id}`}>
                                                    {client?.libelle}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={6} md={6}>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        sx={{ marginTop: 1 }}
                                    >
                                        <InputLabel
                                            htmlFor="operation"
                                            color="warning"
                                            shrink={CheckAttr(extractionData?.operation)}
                                        >
                                            Opération
                                        </InputLabel>
                                        <Select
                                            id="operation"
                                            native
                                            color="warning"
                                            value={extractionData?.operation}
                                            onChange={handleChange}
                                            label="Opération"
                                            inputProps={{
                                                name: "operation",
                                            }}
                                        >
                                            <option value={undefined}></option>
                                            {dataOperations.map((operation) => (
                                                <option key={operation?.id} value={(operation?.is_operationBudget === 1) ? `budget_${operation?.id}` : `prod_${operation?.id}`}>
                                                    {operation?.libelle}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>

                    <CardActions sx={{ mt: 5, mb: 3 }}>
                        <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
                            <Grid item xs={12} sm={12} md={12}>
                                <Box display="flex" justifyContent="center">
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        size="large"
                                        sx={{ textTransform: 'none' }}
                                        endIcon={<FileDownloadIcon />}
                                        onClick={() => handleExtract()}
                                    >
                                        Extraire
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardActions>
                </Card>
            </Container>

            {stackAlert}
            <div>
                <Backdrop
                    sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={loading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </div>
        </>
    );
};

export default ExtractionBudget;